import { useEffect, useRef } from "react";
import { X } from "lucide-react";
import { AnimatePresence, motion, useReducedMotion } from "framer-motion";

export type ModalVariant = "default" | "danger" | "warning" | "success";

export interface ModalAction {
  label: string;
  variant?: "primary" | "secondary" | "ghost" | "danger";
  onPress: () => void;
  disabled?: boolean;
}

export interface IsaleModalProps {
  visible: boolean;
  onDismiss: () => void;
  title?: string;
  subtitle?: string;
  children?: React.ReactNode;
  actions?: ModalAction[];
  variant?: ModalVariant;
  hideDismiss?: boolean;
  dismissOnBackdropPress?: boolean;
  /** Web only — max-width override (default ~420px for centered dialog). */
  maxWidth?: number | string;
}

// ADR-011: token-mapped, theme-aware classes (no raw hex / palette families).
const VARIANT_TITLE_CLASS: Record<ModalVariant, string> = {
  default: "text-primary",
  danger: "text-danger",
  warning: "text-warn",
  success: "text-success",
};

const VARIANT_BUTTON_PRIMARY: Record<ModalVariant, string> = {
  default: "bg-primary-fill hover:bg-primary-fill-pressed",
  danger: "bg-danger hover:brightness-110",
  warning: "bg-warn hover:brightness-110",
  success: "bg-success hover:brightness-110",
};

/**
 * Centered modal for web — keyboard-trap, ESC dismiss, ARIA-conformant.
 *
 * Same prop shape as mobile <IsaleModal /> so call sites share patterns.
 * Mobile uses bottom-sheet anchored to bottom of screen; web uses
 * centered overlay with backdrop blur.
 *
 * Layout structure (Vincent feedback 2026-05-22 PM, iPhone 16 Pro Max):
 *   - Outer wrapper: fixed inset-0 + safe-area-inset padding
 *   - Dialog: maxHeight 90dvh inline (dynamic viewport, handles iOS URL
 *     bar collapse correctly) so it never exceeds viewport
 *   - Flex column: header (fixed) · body (scrollable middle) · actions
 *     (fixed bottom). When body content overflows, only body scrolls;
 *     header + actions stay anchored.
 *   - Body lock: document.body.style.overflow = hidden while modal open
 *     prevents iOS "page zoom out" perception (layout shift from URL bar
 *     collapsing as body scroll position changes).
 */
export function IsaleModal({
  visible,
  onDismiss,
  title,
  subtitle,
  children,
  actions,
  variant = "default",
  hideDismiss = false,
  dismissOnBackdropPress = true,
  maxWidth = 420,
}: IsaleModalProps) {
  const dialogRef = useRef<HTMLDivElement | null>(null);

  // ESC to dismiss + focus trap to dialog when visible.
  useEffect(() => {
    if (!visible) return;
    function handleKey(e: KeyboardEvent) {
      if (e.key === "Escape") {
        e.preventDefault();
        onDismiss();
      }
      // Naive focus trap: keep Tab inside the dialog
      if (e.key === "Tab" && dialogRef.current) {
        const focusables = dialogRef.current.querySelectorAll<HTMLElement>(
          'button:not([disabled]),[href],input,select,textarea,[tabindex]:not([tabindex="-1"])',
        );
        if (focusables.length === 0) return;
        const first = focusables[0]!;
        const last = focusables[focusables.length - 1]!;
        if (e.shiftKey && document.activeElement === first) {
          e.preventDefault();
          last.focus();
        } else if (!e.shiftKey && document.activeElement === last) {
          e.preventDefault();
          first.focus();
        }
      }
    }
    document.addEventListener("keydown", handleKey);
    // Auto-focus first focusable on open
    setTimeout(() => {
      const first = dialogRef.current?.querySelector<HTMLElement>(
        'button:not([disabled]),[href],input,select,textarea,[tabindex]:not([tabindex="-1"])',
      );
      first?.focus();
    }, 50);
    return () => document.removeEventListener("keydown", handleKey);
  }, [visible, onDismiss]);

  // Body scroll lock — prevents iOS Safari "zoom out / layout shift"
  // perception when modal opens (URL bar collapse interaction). Restore
  // previous overflow value on unmount so nested modals don't leak state.
  useEffect(() => {
    if (!visible) return;
    const prevOverflow = document.body.style.overflow;
    const prevPaddingRight = document.body.style.paddingRight;
    // Compensate for scrollbar disappearing on desktop (prevents content
    // jump). innerWidth - documentElement.clientWidth = scrollbar width.
    const scrollbarWidth =
      window.innerWidth - document.documentElement.clientWidth;
    if (scrollbarWidth > 0) {
      document.body.style.paddingRight = `${scrollbarWidth}px`;
    }
    document.body.style.overflow = "hidden";
    return () => {
      document.body.style.overflow = prevOverflow;
      document.body.style.paddingRight = prevPaddingRight;
    };
  }, [visible]);

  // GA-DS-02: respect prefers-reduced-motion — skip animations for users
  // who have requested reduced motion in their OS accessibility settings.
  const prefersReduced = useReducedMotion();
  const backdropVariants = {
    hidden: { opacity: 0 },
    visible: { opacity: 1 },
  };
  const panelVariants = {
    hidden: { opacity: prefersReduced ? 1 : 0, y: prefersReduced ? 0 : 40 },
    visible: { opacity: 1, y: 0 },
    exit: { opacity: prefersReduced ? 1 : 0, y: prefersReduced ? 0 : 24 },
  };

  return (
    <AnimatePresence>
      {visible && (
    <motion.div
      key="isale-modal-backdrop"
      variants={backdropVariants}
      initial="hidden"
      animate="visible"
      exit="hidden"
      transition={{ duration: prefersReduced ? 0 : 0.18, ease: "easeOut" }}
      // Mobile (<md): anchor to bottom (items-end) so the dialog slides up
      // as a bottom-sheet per ADR-006 §Modal pattern. Desktop (md+): center.
      // Padding: edge-to-edge on mobile so the sheet hugs the bottom; only
      // the bottom needs safe-area inset there. Desktop keeps all-around
      // padding so the centered dialog has breathing room.
      className="fixed inset-0 z-50 flex items-end justify-center bg-black/60 backdrop-blur-md md:items-center"
      style={{
        paddingTop: "max(0px, env(safe-area-inset-top))",
        paddingBottom: "max(0px, env(safe-area-inset-bottom))",
        // Padding-x: 0 on mobile (sheet hugs edges), 16px+ on desktop via
        // the md:p-4 class below. We apply via inline style + Tailwind
        // because the safe-area envs need raw values.
      }}
      onClick={dismissOnBackdropPress ? onDismiss : undefined}
      role="dialog"
      aria-modal="true"
      aria-labelledby={title ? "isale-modal-title" : undefined}
      aria-describedby={subtitle ? "isale-modal-subtitle" : undefined}
    >
      {/* Backdrop merged into wrapper itself (bg-black/60 + backdrop-blur-md
          on the fixed inset-0 wrapper). Previously a separate
          `<div className="absolute inset-0 ...">` child was used, but
          Vincent screenshot iPhone 16 Pro Max 2026-05-22 PM showed the
          dim "didn't fullfill backdrop" — the wrapper's padding box vs
          backdrop child created visual gap. Merging eliminates any
          coverage gap regardless of wrapper padding / safe-area. */}

      {/* Dialog — flex column with scrollable body + fixed header/actions.
          Mobile: rounded TOP corners only (bottom-sheet hugs viewport edge);
          desktop: fully rounded with max-width cap.
          max-h uses dvh (dynamic viewport height) to handle iOS URL bar
          collapse correctly. Falls back to vh on older browsers.
          stopPropagation prevents click-through-dismiss when user taps
          inside the modal body.
          GA-DS-02: motion.div gives the panel its own enter (slide-up) +
          exit (fade + slight drop) animation independent of the backdrop. */}
      <motion.div
        ref={dialogRef}
        key="isale-modal-panel"
        variants={panelVariants}
        initial="hidden"
        animate="visible"
        exit="exit"
        transition={{ duration: prefersReduced ? 0 : 0.22, ease: [0, 0, 0.2, 1] }}
        className="relative flex w-full flex-col overflow-hidden rounded-t-3xl bg-elevated shadow-2xl md:rounded-3xl"
        style={{
          maxWidth: typeof maxWidth === "number" ? `${maxWidth}px` : maxWidth,
          maxHeight: "90dvh",
        }}
        onClick={(e) => e.stopPropagation()}
      >
        {/* Drag-handle pill (mobile bottom-sheet only) — visual cue that
            the sheet can be swiped down. Pure decoration; actual swipe
            isn't wired up yet (backdrop tap + Esc + close X all dismiss). */}
        <div
          aria-hidden="true"
          className="mx-auto mt-2 h-1 w-10 shrink-0 rounded-full bg-border md:hidden"
        />

        {/* Header (fixed at top — no scroll) */}
        {(title || !hideDismiss) && (
          <div className="flex flex-shrink-0 items-start justify-between gap-3 px-6 pb-3 pt-5">
            {title && (
              <div className="flex-1">
                <h2
                  id="isale-modal-title"
                  className={`text-xl font-bold leading-tight ${VARIANT_TITLE_CLASS[variant]}`}
                >
                  {title}
                </h2>
                {subtitle && (
                  <p id="isale-modal-subtitle" className="mt-1 text-sm text-fg-subtle">
                    {subtitle}
                  </p>
                )}
              </div>
            )}
            {!hideDismiss && (
              <button
                type="button"
                onClick={onDismiss}
                className="flex h-8 w-8 items-center justify-center rounded-full text-fg-muted hover:bg-surface"
                aria-label="Đóng"
              >
                <X size={18} />
              </button>
            )}
          </div>
        )}

        {/* Body (scrollable middle — flex-1 expands to fill space, overflow-y-auto
            shows scrollbar only when content overflows max-height) */}
        {children && (
          <div className="flex-1 overflow-y-auto px-6 py-2 text-sm text-fg">
            {children}
          </div>
        )}

        {/* Actions (fixed at bottom — no scroll. Border-top divides body
            from action footer when body content is scrolled. */}
        {actions && actions.length > 0 && (
          <div className="flex flex-shrink-0 flex-col gap-2 border-t border-subtle px-6 pb-6 pt-4">
            {actions.map((a, i) => {
              const isPrimary =
                (a.variant ?? (i === 0 ? "primary" : "ghost")) === "primary";
              const isDanger = a.variant === "danger";
              const isSecondary = a.variant === "secondary";
              const classes = isDanger
                ? "bg-danger text-white hover:brightness-110"
                : isPrimary
                  ? `text-white ${VARIANT_BUTTON_PRIMARY[variant]}`
                  : isSecondary
                    ? "border border-primary bg-canvas text-primary hover:bg-primary-soft"
                    : "bg-transparent text-fg-muted hover:bg-surface";
              return (
                <button
                  key={`${a.label}-${i}`}
                  type="button"
                  disabled={a.disabled}
                  onClick={a.onPress}
                  className={`h-12 w-full rounded-full text-sm font-bold transition disabled:opacity-50 ${classes}`}
                >
                  {a.label}
                </button>
              );
            })}
          </div>
        )}
      </motion.div>
    </motion.div>
      )}
    </AnimatePresence>
  );
}
